import { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Form, Button, Modal } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { IconChevronDown, IconChevronUp, IconSelector, IconDotsVertical, IconTrash } from '@tabler/icons-react';
import Sidebar from '../views/components/Sidebar';
import Topbar from '../views/components/Topbar';
import DeacomSearchSelect from '../views/components/DeacomSearchSelect';
import Pagination from '../views/components/Pagination';
import {
    parseOrderPdf, saveOrderSubmission, getOrderSubmissions, updateSubmissionStatus,
    searchDeacomCustomers, searchDeacomShipTos, searchDeacomSkus, getDeacomShipVias, getDeacomTerms, getDeacomBillTo,
    downloadOrderEdi, getOrderSubmission, updateOrderSubmission, getOrderSubmissionPdfBlob, getItemMasterLastUpdated,
} from '../data/dataApi';

// A price mismatch only counts against an automatic *exact* or *sized* SKU
// match - once a CSR manually picks/enters a DeacomID we no longer have grounds
// to second-guess their judgment against whatever price we happened to guess
// earlier. Compares the PO's own line total against (quantity × Deacom list
// price) rather than a per-unit price comparison - Deacom doesn't carry a
// reliable per-unit price for every product, only a price for the specific
// package/SKU sold. Mirrors server/utils/deacom-edi.js's lineItemHasPriceMismatch.
const lineItemHasPriceMismatch = item => {
    if (!['exact', 'sized'].includes(item?.deacomItemMatchType)) return false;
    if (item?.deacomListPrice == null) return false;

    const total = Number(item?.total);
    const quantity = Number(item?.quantity);
    if (!Number.isFinite(total) || !Number.isFinite(quantity)) return false;

    const expected = quantity * Number(item.deacomListPrice);
    return Math.round(total * 100) !== Math.round(expected * 100);
};

const emptyLineItem = () => ({ id: uuidv4(), description: '', sku: '', quantity: 1, unitPrice: 0, total: 0 });

const sumLineItems = items =>
    Math.round(items.reduce((acc, item) => acc + (isNaN(item.total) ? 0 : item.total), 0) * 100) / 100;

const normalizePhone = raw => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11 && digits[0] === '1') return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    return raw;
};

// Required for the EDI file - mirrors every populated (non-blank) column in the
// Deacom sample file, except Ship_To_ID, which trades off against a manual address.
const isAddressComplete = address =>
    Boolean(address?.name?.trim() && address?.street?.trim() && address?.city?.trim() && address?.state?.trim() && address?.zip?.trim() && address?.country?.trim());

const validateOrder = order => {
    const lineItems = order.lineItems.map(item => ({
        id: item.id,
        missingDeacomId: !item.deacomItemId,
        missingQuantity: !(Number(item.deacomQuantity ?? item.quantity) > 0),
    }));

    const validation = {
        orderNumber: !order.orderNumber?.trim(),
        attnLine: !order.customer?.name?.trim(),
        billTo: !order.deacom?.billToId,
        cpId: !order.deacom?.cpId,
        shipTo: !order.deacom?.shipToId && !isAddressComplete(order.shippingAddress),
        shipVia: !order.deacom?.shipViaId,
        termsCode: !order.deacom?.termsCodeId,
        lineItems,
    };

    validation.hasErrors = validation.orderNumber || validation.attnLine || validation.billTo || validation.cpId
        || validation.shipTo || validation.shipVia || validation.termsCode
        || lineItems.some(item => item.missingDeacomId || item.missingQuantity);

    return validation;
};

const RequiredMark = () => <span className="required-mark">*</span>;

const RowActionsMenu = ({ pdfHref, onDownloadEdi }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="row-actions-menu" ref={containerRef} onClick={e => e.stopPropagation()}>
            <button type="button" className="row-actions-toggle" onClick={() => setOpen(o => !o)} aria-label="Row actions">
                <IconDotsVertical size={18} />
            </button>
            {
                open &&
                <div className="row-actions-dropdown">
                    <a className="row-actions-item" href={pdfHref} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>View PDF</a>
                    <button type="button" className="row-actions-item" onClick={() => { setOpen(false); onDownloadEdi(); }}>Download EDI</button>
                </div>
            }
        </div>
    );
};

const ValidationBanner = ({ messages }) => {
    if (!messages || messages.length === 0) return null;
    return (
        <div className="validation-banner tif-mb-15">
            {messages.map(message => <p key={message} className="tif-mb-0">{message}</p>)}
        </div>
    );
};

const STATUS_CLASS = {
    'Needs Review': 'needs-review',
    'Ready to Submit': 'ready-to-submit',
    'Sent to Deacom': 'sent-to-deacom',
    'Confirmed': 'confirmed',
    'Rejected': 'rejected',
};

const STATUS_OPTIONS = ['Needs Review', 'Ready to Submit', 'Sent to Deacom', 'Confirmed', 'Rejected'];

// Transitioning into one of these asserts the order is Deacom-ready - mirrors
// VALIDATED_STATUSES in server/index.js.
const VALIDATED_STATUSES = ['Ready to Submit', 'Sent to Deacom', 'Confirmed'];

const initials = name =>
    name.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase();

const formatDateTime = value => {
    const date = new Date(value);
    if (isNaN(date)) return value;
    return date.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

const SUBMISSIONS_LIST_LIMIT = 20;

const PdfOrderParser = () => {
    const [showModal, setShowModal] = useState(false);
    const [file, setFile] = useState(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [sameAsBilling, setSameAsBilling] = useState(false);
    const [showShippingAddress, setShowShippingAddress] = useState(false);
    const [validationAttempted, setValidationAttempted] = useState(false);
    const [taxRate, setTaxRate] = useState('');
    const [status, setStatus] = useState('Needs Review');
    const [deacomSoNumber, setDeacomSoNumber] = useState('');
    const [saving, setSaving] = useState(false);
    const [submissions, setSubmissions] = useState([]);
    const [deacomShipVias, setDeacomShipVias] = useState([]);
    const [deacomTerms, setDeacomTerms] = useState([]);
    const [itemMasterLastUpdated, setItemMasterLastUpdated] = useState(null);
    const [submissionId, setSubmissionId] = useState(null);
    const [loadingSubmission, setLoadingSubmission] = useState(false);
    const [sortColumn, setSortColumn] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const taxRateRef = useRef('');
    const manualPdfBlobUrlRef = useRef(null);
    const searchDebounceRef = useRef(null);
    const { page } = useParams();
    const navigate = useNavigate();

    const loadSubmissions = useCallback(async () => {
        const response = await getOrderSubmissions(page === undefined ? 0 : page - 1, sortColumn, sortDirection, search);
        if (response.success) setSubmissions(response.data);
    }, [page, sortColumn, sortDirection, search]);

    const handleSearchChange = value => {
        setSearchInput(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setSearch(value.trim());
            if (page !== undefined) navigate('/pdf-order-parser');
        }, 300);
    };

    const handleSort = column =>
        setSortDirection(prevDirection => {
            if (sortColumn === column) return prevDirection === 'asc' ? 'desc' : 'asc';
            setSortColumn(column);
            return 'asc';
        });

    const sortIcon = column => sortColumn === column
        ? (sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />)
        : <IconSelector size={14} />;

    useEffect(() => {
        getDeacomShipVias().then(response => { if (response.success) setDeacomShipVias(response.data); });
        getDeacomTerms().then(response => { if (response.success) setDeacomTerms(response.data); });
        getItemMasterLastUpdated().then(response => { if (response.success) setItemMasterLastUpdated(response.data.lastUpdated); });
    }, []);

    const handleStatusChange = async (id, newStatus) => {
        const previous = submissions;
        setSubmissions(prev => prev.map(row => row.id === id ? { ...row, status: newStatus, updated_at: new Date().toISOString() } : row));

        const response = await updateSubmissionStatus(id, newStatus);
        if (!response.success) {
            setSubmissions(previous);
            const missing = response.error?.missing;
            window.alert(missing?.length
                ? `Cannot set status to "${newStatus}" - missing required fields:\n\n${missing.join('\n')}`
                : (response.error?.error || 'Failed to update status.'));
        }
    };

    const handleDownloadEdi = async id => {
        const response = await downloadOrderEdi(id);
        if (!response.success) {
            const missing = response.error?.missing;
            const message = missing?.length
                ? `Cannot generate EDI file - missing required fields:\n\n${missing.join('\n')}`
                : (response.error?.error || 'Failed to generate EDI file.');
            window.alert(message);
            return;
        }

        const row = submissions.find(r => r.id === id);
        if (row && !['Sent to Deacom', 'Confirmed'].includes(row.status)) {
            await handleStatusChange(id, 'Sent to Deacom');
        }
    };

    useEffect(() => {
        loadSubmissions();
    }, [loadSubmissions]);

    useEffect(() => {
        if (!file) return;

        const objectUrl = URL.createObjectURL(file);
        setPdfBlobUrl(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    const handleParse = async pdfFile => {
        setParsing(true);
        setOrder(null);
        setError(null);

        const response = await parseOrderPdf(pdfFile);

        if (response.success) {
            setOrder(response.data);
        } else {
            setError(response.error?.error || 'Failed to parse order PDF.');
        }

        setParsing(false);
    };

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles.length > 0) {
            const droppedFile = acceptedFiles[0];
            if (manualPdfBlobUrlRef.current) {
                URL.revokeObjectURL(manualPdfBlobUrlRef.current);
                manualPdfBlobUrlRef.current = null;
            }
            setSubmissionId(null);
            setShowModal(true);
            setFile(droppedFile);
            setOrder(null);
            setError(null);
            setSameAsBilling(false);
            setShowShippingAddress(false);
            setValidationAttempted(false);
            taxRateRef.current = '';
            setTaxRate('');
            setStatus('Needs Review');
            setDeacomSoNumber('');
            handleParse(droppedFile);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowClick = async row => {
        setSubmissionId(row.id);
        setShowModal(true);
        setFile(null);
        setOrder(null);
        setError(null);
        setSameAsBilling(false);
        setShowShippingAddress(false);
        setValidationAttempted(false);
        taxRateRef.current = '';
        setTaxRate('');
        setStatus(row.status);
        setDeacomSoNumber(row.deacom_so_number || '');
        setLoadingSubmission(true);

        const [submissionResponse, pdfResponse] = await Promise.all([
            getOrderSubmission(row.id),
            getOrderSubmissionPdfBlob(row.id),
        ]);

        if (submissionResponse.success) {
            const data = submissionResponse.data.order_data;
            setOrder(data);
            setStatus(submissionResponse.data.status);
            setDeacomSoNumber(submissionResponse.data.deacom_so_number || '');
            setShowShippingAddress(!data?.deacom?.shipToId);
        } else {
            setError(submissionResponse.error?.error || 'Failed to load submission.');
        }

        if (pdfResponse.success) {
            const url = URL.createObjectURL(pdfResponse.data);
            manualPdfBlobUrlRef.current = url;
            setPdfBlobUrl(url);
        }

        setLoadingSubmission(false);
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop,
        disabled: parsing,
        multiple: false,
        noClick: true,
        noKeyboard: true,
        accept: {
            'application/pdf': ['.pdf'],
        },
    });

    const handleCloseModal = () => {
        setShowModal(false);
        setFile(null);
        setOrder(null);
        setError(null);
        setSameAsBilling(false);
        setShowShippingAddress(false);
        setValidationAttempted(false);
        taxRateRef.current = '';
        setTaxRate('');
        setStatus('Needs Review');
        setDeacomSoNumber('');
        setSubmissionId(null);
        if (manualPdfBlobUrlRef.current) {
            URL.revokeObjectURL(manualPdfBlobUrlRef.current);
            manualPdfBlobUrlRef.current = null;
        }
        setPdfBlobUrl(null);
    };

    const updateField = (key, value) => setOrder(prev => prev ? { ...prev, [key]: value } : prev);

    const updateNested = (section, key, value) =>
        setOrder(prev => prev ? { ...prev, [section]: { ...prev[section], [key]: value } } : prev);

    const updateLineItem = (id, key, value) =>
        setOrder(prev => prev
            ? { ...prev, lineItems: prev.lineItems.map(item => item.id === id ? { ...item, [key]: value } : item) }
            : prev
        );

    const updateLineItemNumeric = (id, key, rawValue) => {
        const value = rawValue === '' ? NaN : parseFloat(rawValue);
        setOrder(prev => {
            if (!prev) return prev;
            const lineItems = prev.lineItems.map(item => {
                if (item.id !== id) return item;
                const updated = { ...item, [key]: value };
                // Only a Unit Price edit recomputes Total - a Quantity edit leaves
                // Total anchored to whatever it was, so a quantity change that isn't
                // matched by a corresponding total change shows up as a genuine
                // price mismatch (X units of product Y should cost quantity ×
                // Deacom's price; changing X without updating the total no longer
                // adds up, and that's the point).
                if (key === 'unitPrice') {
                    const qty = isNaN(updated.quantity) ? 0 : updated.quantity;
                    const price = isNaN(updated.unitPrice) ? 0 : updated.unitPrice;
                    updated.total = Math.round(qty * price * 100) / 100;
                }
                return updated;
            });
            const newSubtotal = sumLineItems(lineItems);
            const rate = taxRateRef.current;
            const newTax = (typeof rate === 'number' && rate > 0)
                ? Math.round(newSubtotal * rate / 100 * 100) / 100
                : prev.tax;
            return { ...prev, lineItems, subtotal: newSubtotal, tax: newTax };
        });
    };

    const updateDeacom = (key, value) =>
        setOrder(prev => prev ? { ...prev, deacom: { ...prev.deacom, [key]: value } } : prev);

    const handleCustomerSelect = async candidate => {
        setOrder(prev => prev ? {
            ...prev,
            deacom: {
                ...prev.deacom,
                billToId: candidate.deacom_bill_to || null,
                cpId: candidate.deacom_customer_id || null,
                customerLabel: candidate.billing_company || candidate.display_name || candidate.user_email,
                customerMatchType: 'manual',
            },
        } : prev);

        if (candidate.deacom_bill_to) {
            const response = await getDeacomBillTo(candidate.deacom_bill_to);
            if (response.success) updateDeacom('termsCodeId', response.data.terms ? parseInt(response.data.terms, 10) : null);
        }
    };

    const clearCustomerMatch = () => {
        setOrder(prev => prev ? { ...prev, deacom: { ...prev.deacom, billToId: null, cpId: null, customerLabel: null, customerMatchType: null } } : prev);
    };

    const handleCustomerManualEntry = billToId => {
        setOrder(prev => prev ? {
            ...prev,
            deacom: { ...prev.deacom, billToId, cpId: null, customerLabel: 'Manually entered', customerMatchType: 'manual' },
        } : prev);
    };

    const handleShipToSelect = candidate => {
        setShowShippingAddress(false);
        setOrder(prev => prev ? {
            ...prev,
            deacom: {
                ...prev.deacom,
                shipToId: candidate.shipto_id,
                shipToLabel: candidate.name,
                shipToMatchType: 'manual',
                shipToAddress: {
                    name: candidate.name || '',
                    street: candidate.street_1 || '',
                    city: candidate.city || '',
                    state: candidate.state || '',
                    zip: candidate.zip || '',
                    country: candidate.country || '',
                },
            },
        } : prev);
    };

    const clearShipToMatch = () => {
        setShowShippingAddress(true);
        setOrder(prev => prev ? { ...prev, deacom: { ...prev.deacom, shipToId: null, shipToLabel: null, shipToMatchType: null, shipToAddress: null } } : prev);
    };

    const handleShipToManualEntry = shipToId => {
        setShowShippingAddress(false);
        setOrder(prev => prev ? {
            ...prev,
            deacom: { ...prev.deacom, shipToId, shipToLabel: 'Manually entered', shipToMatchType: 'manual', shipToAddress: null },
        } : prev);
    };

    const handleShowShippingAddressChange = checked => {
        setShowShippingAddress(checked);
        if (checked && order?.deacom?.shipToAddress) {
            setOrder(prev => prev ? { ...prev, shippingAddress: { ...prev.deacom.shipToAddress } } : prev);
        }
    };

    const handleSkuSelect = (itemId, candidate) =>
        setOrder(prev => prev
            ? {
                ...prev,
                lineItems: prev.lineItems.map(item => item.id === itemId
                    ? { ...item, deacomItemId: candidate.deacom_id, deacomItemSku: candidate.sku, deacomItemMatchType: 'manual' }
                    : item
                ),
            }
            : prev
        );

    const clearSkuMatch = itemId =>
        setOrder(prev => prev
            ? {
                ...prev,
                lineItems: prev.lineItems.map(item => item.id === itemId
                    ? { ...item, deacomItemId: null, deacomItemSku: null, deacomItemMatchType: null }
                    : item
                ),
            }
            : prev
        );

    const handleSkuManualEntry = (itemId, deacomItemId) =>
        setOrder(prev => prev
            ? {
                ...prev,
                lineItems: prev.lineItems.map(item => item.id === itemId
                    ? { ...item, deacomItemId, deacomItemSku: null, deacomItemMatchType: 'manual' }
                    : item
                ),
            }
            : prev
        );

    // Swaps a line item to one of the auto-computed pack-size suggestions - no
    // typing required. Quantity/Unit Price are set to Deacom's own package
    // count/price (matching how a "sized" match is set up at parse time), so
    // the form always shows the same numbers that get submitted to Deacom.
    const handleSkuSuggestionSelect = (itemId, suggestion) =>
        setOrder(prev => {
            if (!prev) return prev;
            const lineItems = prev.lineItems.map(item => item.id === itemId
                ? {
                    ...item,
                    deacomItemId: suggestion.deacom_id,
                    deacomItemSku: suggestion.sku,
                    deacomItemMatchType: 'sized',
                    deacomListPrice: suggestion.list_price,
                    quantity: suggestion.packages,
                    unitPrice: suggestion.list_price,
                    total: suggestion.total,
                }
                : item
            );
            const newSubtotal = sumLineItems(lineItems);
            const rate = taxRateRef.current;
            const newTax = (typeof rate === 'number' && rate > 0)
                ? Math.round(newSubtotal * rate / 100 * 100) / 100
                : prev.tax;
            return { ...prev, lineItems, subtotal: newSubtotal, tax: newTax };
        });

    const handleTaxRateChange = raw => {
        const rate = raw === '' ? '' : parseFloat(raw);
        taxRateRef.current = rate;
        setTaxRate(rate);
        if (typeof rate === 'number' && !isNaN(rate) && order) {
            const subtotal = isNaN(order.subtotal) ? 0 : order.subtotal;
            updateField('tax', Math.round(subtotal * rate / 100 * 100) / 100);
        }
    };

    const handleSubtotalChange = raw => {
        const subtotal = raw === '' ? NaN : parseFloat(raw);
        const rate = taxRateRef.current;
        const newTax = (typeof rate === 'number' && rate > 0 && !isNaN(subtotal))
            ? Math.round(subtotal * rate / 100 * 100) / 100
            : order?.tax ?? NaN;
        setOrder(prev => prev ? { ...prev, subtotal, tax: newTax } : prev);
    };

    const addLineItem = () =>
        setOrder(prev => {
            if (!prev) return prev;
            const lineItems = [...prev.lineItems, emptyLineItem()];
            return { ...prev, lineItems, subtotal: sumLineItems(lineItems) };
        });

    const removeLineItem = id =>
        setOrder(prev => {
            if (!prev) return prev;
            const lineItems = prev.lineItems.filter(item => item.id !== id);
            return { ...prev, lineItems, subtotal: sumLineItems(lineItems) };
        });

    const handleSameAsBillingChange = checked => {
        setSameAsBilling(checked);
        if (checked) {
            setOrder(prev => prev ? { ...prev, shippingAddress: { ...prev.billingAddress } } : prev);
        }
    };

    const computedTotal = order
        ? Math.round(((Number(order.subtotal) || 0) + (Number(order.tax) || 0) + (Number(order.shipping) || 0) - (Number(order.discount) || 0)) * 100) / 100
        : 0;

    const validation = order ? validateOrder(order) : null;

    const handleSave = async () => {
        if (!order || (!submissionId && !file)) return;

        if (VALIDATED_STATUSES.includes(status) && validation?.hasErrors) {
            setValidationAttempted(true);
            setError(`Required fields are missing - see the highlighted sections below before marking this order "${status}".`);
            return;
        }

        setSaving(true);
        setError(null);

        const response = submissionId
            ? await updateOrderSubmission(submissionId, { ...order, total: computedTotal }, status, deacomSoNumber)
            : await saveOrderSubmission(file, { ...order, total: computedTotal }, status, deacomSoNumber);

        if (response.success) {
            await loadSubmissions();
            handleCloseModal();
        } else {
            setError(response.error?.error || 'Failed to save submission.');
        }

        setSaving(false);
    };

    return (
        <div id="pdf-order-parser" className="d-flex" {...getRootProps()}>
            <input {...getInputProps()} />
            <Sidebar />
            <div className="flex-grow-1">
                <Topbar title="PDF Order Parser" backButton />
                <div className="content-wrapper tif-pt-md-50 tif-pt-25 tif-px-md-60 tif-px-30 tif-pb-85 tif-pb-45">
                    <div className="submissions-header tif-mb-10">
                        <div>
                            <h4 className="tif-mb-5">Submissions</h4>
                            <p className="tif-font-sm tif-color-oscar tif-mb-0">Drag and drop a purchase order PDF anywhere on this page for AI field extraction.</p>
                            <p className="tif-font-sm tif-color-oscar tif-mb-0">
                                ItemMaster - Last Updated: {itemMasterLastUpdated ? formatDateTime(itemMasterLastUpdated) : 'Never'}
                            </p>
                        </div>
                        <div className="submissions-actions">
                            <Link to="/pdf-order-parser/deacom-confirmations">
                                <Button className="deacom-confirmations-btn">DEACOM Confirmations</Button>
                            </Link>
                            <Button className="dashboard-quarternary-btn" onClick={open}>Upload PDF</Button>
                        </div>
                    </div>

                    <div className="submissions-search tif-mb-15">
                        <input
                            type="text"
                            className="submissions-search-input"
                            placeholder="Search by CSR, Customer, Order ID, or Web Order ID..."
                            value={searchInput}
                            onChange={e => handleSearchChange(e.target.value)}
                        />
                    </div>

                    <div className="submissions-table-wrapper">
                        <table className="submissions-table w-100">
                            <thead>
                                <tr>
                                    <th className="sortable-column" onClick={() => handleSort('csr')}>CSR {sortIcon('csr')}</th>
                                    <th className="sortable-column" onClick={() => handleSort('customer_name')}>Customer {sortIcon('customer_name')}</th>
                                    <th className="sortable-column" onClick={() => handleSort('status')}>Status {sortIcon('status')}</th>
                                    <th>Deacom Ready</th>
                                    <th>Price Flag</th>
                                    <th className="sortable-column" onClick={() => handleSort('created_at')}>Created {sortIcon('created_at')}</th>
                                    <th className="sortable-column" onClick={() => handleSort('order_number')}>Order ID {sortIcon('order_number')}</th>
                                    <th className="sortable-column" onClick={() => handleSort('web_order_id')}>Web Order ID {sortIcon('web_order_id')}</th>
                                    <th className="sortable-column" onClick={() => handleSort('order_total')}>Order Total {sortIcon('order_total')}</th>
                                    <th className="sortable-column" onClick={() => handleSort('modified')}>Modified {sortIcon('modified')}</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    submissions.length === 0 ?
                                        <tr>
                                            <td colSpan="11" className="text-center tif-color-oscar tif-py-30">No submissions yet.</td>
                                        </tr>
                                    :
                                        submissions.map(row => (
                                            <tr key={row.id} className="clickable" onClick={() => handleRowClick(row)}>
                                                <td data-label="CSR">
                                                    <div className="csr-cell">
                                                        <span className="csr-avatar">{initials(row.csr || '')}</span>
                                                        <span>{row.csr}</span>
                                                    </div>
                                                </td>
                                                <td className="customer-cell" data-label="Customer" title={row.customer_name || ''}>{row.customer_name || <span className="empty-value">-</span>}</td>
                                                <td data-label="Status" onClick={e => e.stopPropagation()}>
                                                    <div className={`status-select-wrapper ${STATUS_CLASS[row.status] || ''}`}>
                                                        <select
                                                            className="status-select"
                                                            value={row.status}
                                                            onChange={e => handleStatusChange(row.id, e.target.value)}
                                                        >
                                                            {STATUS_OPTIONS.map(opt => (
                                                                <option key={opt} value={opt}>{opt}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </td>
                                                <td data-label="Deacom Ready">
                                                    <span className={`ready-badge ${row.deacom_ready ? 'ready' : 'incomplete'}`}>
                                                        {row.deacom_ready ? 'Ready' : 'Incomplete'}
                                                    </span>
                                                </td>
                                                <td data-label="Price Flag">
                                                    {
                                                        row.price_checked &&
                                                        <span className={`ready-badge ${row.price_mismatch ? 'incomplete' : 'ready'}`}>
                                                            {row.price_mismatch ? 'Mismatch' : 'Match'}
                                                        </span>
                                                    }
                                                </td>
                                                <td className="tif-color-oscar" data-label="Created">{formatDateTime(row.created_at)}</td>
                                                <td className="order-id-cell" data-label="Order ID">{row.order_number || <span className="empty-value">-</span>}</td>
                                                <td className="tif-color-oscar" data-label="Web Order ID">{row.web_order_id}</td>
                                                <td data-label="Order Total">{typeof row.order_total === 'number' ? `$${row.order_total.toFixed(2)}` : <span className="empty-value">-</span>}</td>
                                                <td className="tif-color-oscar" data-label="Modified">{formatDateTime(row.updated_at)}</td>
                                                <td className="text-end" data-label="Actions">
                                                    <RowActionsMenu
                                                        pdfHref={`${process.env.REACT_APP_API_URL}/api/orders/submissions/${row.id}/pdf`}
                                                        onDownloadEdi={() => handleDownloadEdi(row.id)}
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-center tif-mt-20">
                        <Pagination
                            listLength={submissions.length && submissions[0].total_count}
                            path="/pdf-order-parser"
                            page={page}
                            limit={SUBMISSIONS_LIST_LIMIT}
                        />
                    </div>
                </div>
            </div>

            <Modal className="pdf-order-parser-modal" show={showModal} onHide={handleCloseModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{submissionId ? 'Edit Submission' : 'Parse Order PDF'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <p className="error-text">{error}</p>}

                    {
                        parsing && !order &&
                        <p className="tif-color-golf">Parsing {file?.name}...</p>
                    }
                    {
                        loadingSubmission && !order &&
                        <p className="tif-color-golf">Loading submission...</p>
                    }

                    <div className="d-flex tif-pdf-parser-split">
                        <div className="tif-pdf-parser-form">
                        {
                            order &&
                            <Form className="secondary-form tif-mb-40">
                            <p className="required-legend tif-mb-15"><RequiredMark /> Required for Deacom submission</p>
                            <div className="card tif-p-25 tif-mb-25">
                                <h4 className="tif-mb-20">Order Info</h4>
                                {
                                    validationAttempted &&
                                    <ValidationBanner messages={validation.orderNumber ? ['Order Number is required.'] : []} />
                                }
                                <Form.Group className="form-group tif-mb-15" controlId="formOrderNumber">
                                    <Form.Label>Order Number<RequiredMark /></Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.orderNumber}
                                        onChange={e => updateField('orderNumber', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formOrderDate">
                                    <Form.Label>Order Date</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.orderDate}
                                        placeholder="YYYY-MM-DD"
                                        onChange={e => updateField('orderDate', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formStatus">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        className="border-0 fw-normal tif-color-white"
                                        value={status}
                                        onChange={e => setStatus(e.target.value)}
                                    >
                                        {STATUS_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="form-group" controlId="formDeacomSoNumber">
                                    <Form.Label>Deacom SO Number</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        placeholder="Not yet assigned"
                                        value={deacomSoNumber}
                                        onChange={e => setDeacomSoNumber(e.target.value)}
                                    />
                                </Form.Group>
                            </div>

                            <div className="card tif-p-25 tif-mb-25">
                                <h4 className="tif-mb-20">Customer Info</h4>
                                {
                                    validationAttempted &&
                                    <ValidationBanner messages={[
                                        validation.billTo ? 'Deacom Customer / BillTo is required.' : null,
                                        validation.cpId ? 'Customer ID (cp_id) is required - not every BillTo has one on file; enter it manually if the match doesn\'t supply one.' : null,
                                        validation.termsCode ? 'Terms Code is required.' : null,
                                        validation.attnLine ? 'Customer Name is required.' : null,
                                    ].filter(Boolean)} />
                                }
                                {
                                    order.suggestedCustomerName &&
                                    <div className="tif-mb-15">
                                        <span className="suggested-match-badge">Suggested match: {order.suggestedCustomerName}</span>
                                    </div>
                                }
                                <Form.Group className="form-group tif-mb-15" controlId="formDeacomCustomer">
                                    <Form.Label>Deacom Customer / BillTo<RequiredMark /></Form.Label>
                                    <DeacomSearchSelect
                                        placeholder="Search by company or contact name..."
                                        selected={
                                            order.deacom?.billToId
                                                ? {
                                                    primary: order.deacom.customerLabel || 'Matched customer',
                                                    idLabels: [
                                                        `BillToID: ${order.deacom.billToId}`,
                                                        order.deacom.cpId ? `CustomerID: ${order.deacom.cpId}` : null,
                                                    ].filter(Boolean),
                                                    matchType: order.deacom.customerMatchType,
                                                }
                                                : null
                                        }
                                        searchFn={async q => {
                                            const response = await searchDeacomCustomers(q);
                                            if (!response.success) return [];
                                            return response.data.map(c => ({
                                                id: c.wp_user_id,
                                                primary: c.billing_company || c.display_name || c.user_email,
                                                secondary: `BillToID: ${c.deacom_bill_to || 'n/a'}, CustomerID: ${c.deacom_customer_id || 'n/a'}${c.user_email ? `, ${c.user_email}` : ''}`,
                                                ...c,
                                            }));
                                        }}
                                        onSelect={handleCustomerSelect}
                                        onClear={clearCustomerMatch}
                                        onManualEntry={handleCustomerManualEntry}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formDeacomCpId">
                                    <Form.Label>Customer ID (cp_id)<RequiredMark /></Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.deacom?.cpId ?? ''}
                                        placeholder="Not every BillTo has one on file - enter manually if needed"
                                        onChange={e => updateDeacom('cpId', e.target.value.trim() || null)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formTermsCode">
                                    <Form.Label>Terms Code<RequiredMark /></Form.Label>
                                    <Form.Select
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.deacom?.termsCodeId ?? ''}
                                        onChange={e => updateDeacom('termsCodeId', e.target.value ? parseInt(e.target.value, 10) : null)}
                                    >
                                        <option value="">Not set</option>
                                        {deacomTerms
                                            .filter(term => term.enabled || term.terms_id === order.deacom?.termsCodeId)
                                            .map(term => (
                                                <option key={term.terms_id} value={term.terms_id}>{term.name}</option>
                                            ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formCustomerName">
                                    <Form.Label>Name<RequiredMark /></Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.customer.name}
                                        onChange={e => updateNested('customer', 'name', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formCustomerCompany">
                                    <Form.Label>Company</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.customer.company}
                                        onChange={e => updateNested('customer', 'company', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formCustomerEmail">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        type="email"
                                        value={order.customer.email}
                                        onChange={e => updateNested('customer', 'email', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group" controlId="formCustomerPhone">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.customer.phone}
                                        onChange={e => updateNested('customer', 'phone', e.target.value)}
                                        onBlur={e => {
                                            const normalized = normalizePhone(e.target.value);
                                            if (normalized !== e.target.value) updateNested('customer', 'phone', normalized);
                                        }}
                                    />
                                </Form.Group>
                            </div>

                            <div className="card tif-p-25 tif-mb-25">
                                <h4 className="tif-mb-20">Billing Address</h4>
                                <Form.Group className="form-group tif-mb-15" controlId="formBillingName">
                                    <Form.Label>Name / Attention</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.billingAddress.name}
                                        onChange={e => updateNested('billingAddress', 'name', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formBillingStreet">
                                    <Form.Label>Street</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.billingAddress.street}
                                        onChange={e => updateNested('billingAddress', 'street', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formBillingCity">
                                    <Form.Label>City</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.billingAddress.city}
                                        onChange={e => updateNested('billingAddress', 'city', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formBillingState">
                                    <Form.Label>State</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.billingAddress.state}
                                        onChange={e => updateNested('billingAddress', 'state', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formBillingZip">
                                    <Form.Label>ZIP</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.billingAddress.zip}
                                        onChange={e => updateNested('billingAddress', 'zip', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group" controlId="formBillingCountry">
                                    <Form.Label>Country</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.billingAddress.country}
                                        onChange={e => updateNested('billingAddress', 'country', e.target.value)}
                                    />
                                </Form.Group>
                            </div>

                            <div className="card tif-p-25 tif-mb-25">
                                <h4 className="tif-mb-20">Shipping Address</h4>
                                {
                                    validationAttempted &&
                                    <ValidationBanner messages={[
                                        validation.shipTo ? 'ShipTo (or a complete shipping address) is required.' : null,
                                        validation.shipVia ? 'Ship Via is required.' : null,
                                    ].filter(Boolean)} />
                                }
                                <Form.Check
                                    type="checkbox"
                                    label="Same as billing"
                                    className="tif-mb-15"
                                    checked={sameAsBilling}
                                    onChange={e => handleSameAsBillingChange(e.target.checked)}
                                />
                                <Form.Group className="form-group tif-mb-15" controlId="formDeacomShipTo">
                                    <Form.Label>ShipTo<RequiredMark /></Form.Label>
                                    <DeacomSearchSelect
                                        placeholder="Search by name, city, or ZIP..."
                                        selected={
                                            order.deacom?.shipToId
                                                ? {
                                                    primary: order.deacom.shipToLabel || 'Matched shipto',
                                                    idLabel: `ShipToID: ${order.deacom.shipToId}`,
                                                    matchType: order.deacom.shipToMatchType,
                                                }
                                                : null
                                        }
                                        searchFn={async q => {
                                            const response = await searchDeacomShipTos(q, order.deacom?.billToId);
                                            if (!response.success) return [];
                                            return response.data.map(s => ({
                                                id: s.shipto_id,
                                                primary: s.name,
                                                secondary: `ShipToID: ${s.shipto_id}, ${[s.city, s.state, s.zip].filter(Boolean).join(', ')}`,
                                                ...s,
                                            }));
                                        }}
                                        onSelect={handleShipToSelect}
                                        onClear={clearShipToMatch}
                                        onManualEntry={handleShipToManualEntry}
                                    />
                                </Form.Group>
                                {
                                    order.deacom?.shipToId &&
                                    <Form.Check
                                        type="checkbox"
                                        label="Edit shipping address"
                                        className="tif-mb-15"
                                        checked={showShippingAddress}
                                        onChange={e => handleShowShippingAddressChange(e.target.checked)}
                                    />
                                }
                                <Form.Group className="form-group tif-mb-15" controlId="formShipVia">
                                    <Form.Label>Ship Via<RequiredMark /></Form.Label>
                                    <Form.Select
                                        className="border-0 fw-normal tif-color-white"
                                        value={order.deacom?.shipViaId ?? ''}
                                        onChange={e => updateDeacom('shipViaId', e.target.value || null)}
                                    >
                                        <option value="">Not set</option>
                                        {deacomShipVias.map(sv => (
                                            <option key={sv.shipvia_id} value={sv.shipvia_id}>{sv.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                {
                                    (!order.deacom?.shipToId || showShippingAddress) &&
                                    <>
                                        <Form.Group className="form-group tif-mb-15" controlId="formShippingName">
                                            <Form.Label>Name / Attention<RequiredMark /></Form.Label>
                                            <Form.Control
                                                className="border-0 fw-normal tif-color-white"
                                                value={order.shippingAddress.name}
                                                disabled={sameAsBilling}
                                                onChange={e => updateNested('shippingAddress', 'name', e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="form-group tif-mb-15" controlId="formShippingStreet">
                                            <Form.Label>Street<RequiredMark /></Form.Label>
                                            <Form.Control
                                                className="border-0 fw-normal tif-color-white"
                                                value={order.shippingAddress.street}
                                                disabled={sameAsBilling}
                                                onChange={e => updateNested('shippingAddress', 'street', e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="form-group tif-mb-15" controlId="formShippingCity">
                                            <Form.Label>City<RequiredMark /></Form.Label>
                                            <Form.Control
                                                className="border-0 fw-normal tif-color-white"
                                                value={order.shippingAddress.city}
                                                disabled={sameAsBilling}
                                                onChange={e => updateNested('shippingAddress', 'city', e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="form-group tif-mb-15" controlId="formShippingState">
                                            <Form.Label>State<RequiredMark /></Form.Label>
                                            <Form.Control
                                                className="border-0 fw-normal tif-color-white"
                                                value={order.shippingAddress.state}
                                                disabled={sameAsBilling}
                                                onChange={e => updateNested('shippingAddress', 'state', e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="form-group tif-mb-15" controlId="formShippingZip">
                                            <Form.Label>ZIP<RequiredMark /></Form.Label>
                                            <Form.Control
                                                className="border-0 fw-normal tif-color-white"
                                                value={order.shippingAddress.zip}
                                                disabled={sameAsBilling}
                                                onChange={e => updateNested('shippingAddress', 'zip', e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="form-group" controlId="formShippingCountry">
                                            <Form.Label>Country<RequiredMark /></Form.Label>
                                            <Form.Control
                                                className="border-0 fw-normal tif-color-white"
                                                value={order.shippingAddress.country}
                                                disabled={sameAsBilling}
                                                onChange={e => updateNested('shippingAddress', 'country', e.target.value)}
                                            />
                                        </Form.Group>
                                    </>
                                }
                            </div>

                            <div className="card tif-p-25 tif-mb-25">
                                <h4 className="tif-mb-20">Line Items</h4>
                                {
                                    validationAttempted &&
                                    <ValidationBanner messages={
                                        validation.lineItems
                                            .map((li, index) => {
                                                const issues = [];
                                                if (li.missingDeacomId) issues.push('DeacomID');
                                                if (li.missingQuantity) issues.push('Quantity');
                                                return issues.length ? `Item ${index + 1} is missing: ${issues.join(', ')}.` : null;
                                            })
                                            .filter(Boolean)
                                    } />
                                }
                                {
                                    order.lineItems.some(lineItemHasPriceMismatch) &&
                                    <div className="price-mismatch-banner tif-mb-15">
                                        <p className="tif-mb-0">One or more line items don't match Deacom's list price - see the flagged item(s) below.</p>
                                    </div>
                                }
                                {
                                    order.lineItems.map((item, index) => {
                                        const priceCheckApplies = ['exact', 'sized'].includes(item.deacomItemMatchType) && item.deacomListPrice != null;
                                        const priceMismatch = priceCheckApplies && lineItemHasPriceMismatch(item);
                                        return (
                                        <div className="card sub-card tif-p-20 tif-mb-15" key={item.id}>
                                            <div className="d-flex justify-content-between align-items-center tif-mb-15">
                                                <p className="tif-font-sm fw-bold tif-mb-0">Item {index + 1}</p>
                                                <button
                                                    type="button"
                                                    className="line-item-remove-btn"
                                                    onClick={() => removeLineItem(item.id)}
                                                    aria-label="Remove line item"
                                                >
                                                    <IconTrash size={16} />
                                                </button>
                                            </div>
                                            <Form.Group className="form-group tif-mb-15" controlId={`formLineDescription_${index}`}>
                                                <Form.Label>Description</Form.Label>
                                                <Form.Control
                                                    className="border-0 fw-normal tif-color-white"
                                                    value={item.description}
                                                    onChange={e => updateLineItem(item.id, 'description', e.target.value)}
                                                />
                                            </Form.Group>
                                            <Form.Group className="form-group tif-mb-15" controlId={`formLineSku_${index}`}>
                                                <Form.Label>SKU</Form.Label>
                                                <Form.Control
                                                    className="border-0 fw-normal tif-color-white"
                                                    value={item.sku}
                                                    onChange={e => updateLineItem(item.id, 'sku', e.target.value)}
                                                />
                                            </Form.Group>
                                            <Form.Group className="form-group tif-mb-15" controlId={`formLineDeacomItem_${index}`}>
                                                <Form.Label>DeacomID<RequiredMark /></Form.Label>
                                                <DeacomSearchSelect
                                                    placeholder="Search by SKU..."
                                                    selected={
                                                        item.deacomItemId
                                                            ? {
                                                                primary: item.deacomItemSku || item.sku,
                                                                idLabel: `DeacomID: ${item.deacomItemId}`,
                                                                matchType: item.deacomItemMatchType,
                                                            }
                                                            : null
                                                    }
                                                    searchFn={async q => {
                                                        const response = await searchDeacomSkus(q);
                                                        if (!response.success) return [];
                                                        return response.data.map(s => ({
                                                            id: s.deacom_id,
                                                            primary: s.sku,
                                                            secondary: `DeacomID: ${s.deacom_id}${s.product_category ? `, ${s.product_category}` : ''}`,
                                                            ...s,
                                                        }));
                                                    }}
                                                    onSelect={candidate => handleSkuSelect(item.id, candidate)}
                                                    onClear={() => clearSkuMatch(item.id)}
                                                    onManualEntry={value => handleSkuManualEntry(item.id, value)}
                                                />
                                                {
                                                    ['sized', 'fuzzy'].includes(item.deacomItemMatchType) && item.deacomSuggestions?.length > 0 &&
                                                    <div className="sku-suggestions tif-mt-10">
                                                        <p className="sku-suggestions-label tif-mb-5">Additional Suggestions:</p>
                                                        <div className="sku-suggestions-list">
                                                            {item.deacomSuggestions.map(suggestion => {
                                                                const isSelected = suggestion.deacom_id === item.deacomItemId;
                                                                const chipClass = isSelected
                                                                    ? (item.deacomItemMatchType === 'sized' ? 'selected-sized' : 'selected')
                                                                    : '';
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        key={suggestion.deacom_id}
                                                                        className={`sku-suggestion-chip ${chipClass}`}
                                                                        onClick={() => handleSkuSuggestionSelect(item.id, suggestion)}
                                                                    >
                                                                        <span className="sku-suggestion-sku">{suggestion.sku}</span>
                                                                        {
                                                                            suggestion.packages != null &&
                                                                            <span className="sku-suggestion-detail">
                                                                                {suggestion.packages} Qty{suggestion.total != null ? ` = $${suggestion.total.toFixed(2)}` : ''}
                                                                            </span>
                                                                        }
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                }
                                            </Form.Group>
                                            <div className="line-item-totals-section">
                                                {
                                                    priceCheckApplies &&
                                                    <div className="price-check-header">
                                                        <span className={`price-check-badge ${priceMismatch ? 'mismatch' : 'match'}`}>
                                                            {priceMismatch ? 'Price Mismatch' : 'Price Match'}
                                                        </span>
                                                    </div>
                                                }
                                                <div className="line-item-totals-row">
                                                    <Form.Group className="form-group" controlId={`formLineQty_${index}`}>
                                                        <Form.Label>Quantity<RequiredMark /></Form.Label>
                                                        <Form.Control
                                                            className="border-0 fw-normal tif-color-white text-end"
                                                            type="number"
                                                            min={0}
                                                            value={isNaN(item.quantity) ? '' : item.quantity}
                                                            onFocus={e => e.target.select()}
                                                            onChange={e => updateLineItemNumeric(item.id, 'quantity', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="form-group" controlId={`formLinePrice_${index}`}>
                                                        <Form.Label>Unit Price</Form.Label>
                                                        <Form.Control
                                                            className="border-0 fw-normal tif-color-white text-end"
                                                            type="number"
                                                            min={0}
                                                            step={0.01}
                                                            value={isNaN(item.unitPrice) ? '' : item.unitPrice}
                                                            onFocus={e => e.target.select()}
                                                            onChange={e => updateLineItemNumeric(item.id, 'unitPrice', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="form-group" controlId={`formLineTotal_${index}`}>
                                                        <Form.Label>Line Total</Form.Label>
                                                        <div className="line-item-total-box">
                                                            ${Number(item.total).toFixed(2)}
                                                        </div>
                                                    </Form.Group>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })
                                }
                                <Button className="dashboard-senary-btn" onClick={addLineItem}>+ Add Line Item</Button>
                            </div>

                            <div className="card tif-p-25 tif-mb-25">
                                <h4 className="tif-mb-20">Order Totals</h4>
                                <Form.Group className="form-group tif-mb-15" controlId="formSubtotal">
                                    <Form.Label>Subtotal</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        type="number"
                                        min={0}
                                        step={0.01}
                                        value={isNaN(order.subtotal) ? '' : order.subtotal}
                                        onFocus={e => e.target.select()}
                                        onChange={e => handleSubtotalChange(e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formTaxRate">
                                    <Form.Label>Tax Rate (%)</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={0.001}
                                        placeholder="0"
                                        value={taxRate === '' ? '' : taxRate}
                                        onFocus={e => e.target.select()}
                                        onChange={e => handleTaxRateChange(e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formTax">
                                    <Form.Label>Tax</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        type="number"
                                        min={0}
                                        step={0.01}
                                        value={isNaN(order.tax) ? '' : order.tax}
                                        onFocus={e => e.target.select()}
                                        onChange={e => updateField('tax', e.target.value === '' ? NaN : parseFloat(e.target.value))}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-15" controlId="formShipping">
                                    <Form.Label>Shipping</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        type="number"
                                        min={0}
                                        step={0.01}
                                        value={isNaN(order.shipping) ? '' : order.shipping}
                                        onFocus={e => e.target.select()}
                                        onChange={e => updateField('shipping', e.target.value === '' ? NaN : parseFloat(e.target.value))}
                                    />
                                </Form.Group>
                                <Form.Group className="form-group tif-mb-20" controlId="formDiscount">
                                    <Form.Label>Discount</Form.Label>
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        type="number"
                                        min={0}
                                        step={0.01}
                                        value={isNaN(order.discount) ? '' : order.discount}
                                        onFocus={e => e.target.select()}
                                        onChange={e => updateField('discount', e.target.value === '' ? NaN : parseFloat(e.target.value))}
                                    />
                                </Form.Group>
                                <p className="grand-total-row fw-bold tif-mb-0">Total: ${computedTotal.toFixed(2)}</p>
                            </div>

                            <div className="card tif-p-25 tif-mb-25">
                                <h4 className="tif-mb-20">Notes</h4>
                                <Form.Group className="form-group" controlId="formNotes">
                                    <Form.Control
                                        className="border-0 fw-normal tif-color-white"
                                        as="textarea"
                                        rows={3}
                                        value={order.notes}
                                        onChange={e => updateField('notes', e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                        </Form>
                        }
                        </div>

                        <div className="tif-pdf-parser-preview">
                            {
                                pdfBlobUrl &&
                                <object
                                    data={`${pdfBlobUrl}#toolbar=0&navpanes=0`}
                                    type="application/pdf"
                                    className="w-100 h-100"
                                >
                                    <p className="tif-p-20">Unable to display PDF preview.</p>
                                </object>
                            }
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={handleCloseModal} disabled={saving}>Close</Button>
                    <Button className="dashboard-quarternary-btn" onClick={handleSave} disabled={!order || saving}>{saving ? 'Saving...' : (submissionId ? 'Save Changes' : 'Save Submission')}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PdfOrderParser;
